import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Bot, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "What should I reorder at Andheri?",
  "Which reagents expire this month?",
  "Show me everything that is low or out of stock",
  "Any expired stock I need to quarantine?",
];

export default function Assistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useQuery({
    queryKey: ["assistant-history"],
    queryFn: () => api.get("/assistant/history").then((r) => r.data),
    onSuccess: (data) => {
      const hist = [];
      data.forEach((m) => {
        hist.push({ role: "user", text: m.question });
        hist.push({ role: "assistant", text: m.answer });
      });
      setMessages(hist);
    },
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (q) => {
    const question = (q ?? input).trim();
    if (!question || loading) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/ask", { question });
      setMessages((m) => [...m, { role: "assistant", text: data.answer }]);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I couldn't process that. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="assistant-page">
      <div>
        <h2 className="font-serif text-3xl tracking-tight text-navy flex items-center gap-2">
          <Sparkles size={26} style={{ color: "#0a8f8f" }} /> AI Inventory Assistant
        </h2>
        <p className="text-slate-500 text-sm mt-1">Ask about stock, expiry &amp; reorders in plain English — grounded in your live inventory.</p>
      </div>

      <Card className="bg-white border-slate-200 flex flex-col" style={{ height: "calc(100vh - 240px)", minHeight: 420 }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
          {messages.length === 0 && !loading && (
            <div className="h-full flex flex-col items-center justify-center text-center" data-testid="assistant-empty">
              <div className="h-14 w-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: "#0a8f8f18" }}>
                <Bot size={28} style={{ color: "#0a8f8f" }} />
              </div>
              <div className="font-serif text-xl text-navy">How can I help with your inventory?</div>
              <p className="text-slate-500 text-sm mt-1 mb-5">Try one of these:</p>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`assistant-suggestion-${i}`}
                    onClick={() => send(s)}
                    className="text-sm px-3 py-2 rounded-full border border-slate-200 text-slate-600 hover:border-[#0a8f8f] hover:text-[#0a8f8f] transition-colors duration-200"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`assistant-msg-${m.role}`}>
              {m.role === "assistant" && (
                <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#042C53" }}>
                  <Bot size={16} className="text-white" />
                </div>
              )}
              <div
                className="max-w-[78%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed"
                style={m.role === "user"
                  ? { background: "#042C53", color: "#fff", borderBottomRightRadius: 4 }
                  : { background: "#F1F5F9", color: "#0f172a", borderBottomLeftRadius: 4 }}
              >
                {m.text}
              </div>
              {m.role === "user" && (
                <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#0a8f8f" }}>
                  <UserIcon size={16} className="text-white" />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex gap-3" data-testid="assistant-loading">
              <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#042C53" }}>
                <Bot size={16} className="text-white" />
              </div>
              <div className="px-4 py-3 rounded-2xl bg-slate-100 flex gap-1">
                <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 p-4 flex gap-2">
          <Input
            data-testid="assistant-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask about stock, expiry or reorders…"
            className="flex-1"
            disabled={loading}
          />
          <Button data-testid="assistant-send" onClick={() => send()} disabled={loading || !input.trim()} style={{ background: "#042C53" }} className="text-white">
            <Send size={16} />
          </Button>
        </div>
      </Card>
    </div>
  );
}
